
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import * as L from 'leaflet';
import { Position } from '@turf/helpers';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import area from '@turf/area';
import { useFieldStore } from '../stores/fieldStore';
import { Point } from '../models/Point';
import RenderColors from '../services/colors';
import { FieldOperationsModel } from '../models/FieldOperationsModel';

@Component
export default class LeafletMapPolygonEdit extends Vue {
  @Prop() map!: L.Map;

  public fieldStore = useFieldStore();
  polygon!: L.Polygon;

  async mounted() {
    this.addMapClickListener();
  }

  destroyed() {
    this.destroyResources();
  }

  deactivated() {
    this.destroyResources();
  }

  private destroyResources() {
    this.map.off('click');
    this.polygon.off('pm:edit');
  }

  createNewPolygon(path: Point[]) {
    const points = path.map((val) => {
      return { lat: val.Item1, lng: val.Item2 };
    });

    this.polygon = new L.Polygon(points, {
      color: RenderColors.Field,
      fillColor: 'transparent',
      opacity: 0.8,
      weight: 3
    }).addTo(this.map);
    this.$nextTick(() => {
      this.map.fitBounds(this.polygon.getBounds(), { maxZoom: 100 });
    });
    if (this.fieldStore.editFieldMode || this.fieldStore.addFieldMode)
      this.polygon.pm.enable({ removeVertexOn: 'click' });
    else {
      this.polygon.pm.disable();
    }
  }

  @Watch('fieldStore.field')
  onSelectedPathChange(field: FieldOperationsModel): void {
    if (this.polygon) this.polygon.remove();
    if (!field) return;
    this.createNewPolygon(field.BoundaryPoints);

    this.fieldStore.setSelectedFieldArea(this.findFieldArea());
    const polyPoiints = this.polygon.toGeoJSON();
    this.fieldStore.updateCurrentPolygon(polyPoiints.geometry.coordinates as Position[][]);
  }

  @Watch('fieldStore.editMode')
  onEditableChange(): void {
    this.addMapClickListener();
    if (this.polygon) {
      if (this.fieldStore.editFieldMode) this.polygon.pm.enable({ removeVertexOn: 'click' });
      else {
        this.polygon.pm.disable();
      }
    }
  }

  @Watch('fieldStore.revertBoundary')
  onRevertBoundaryChange(): void {
    this.polygon.remove();
    this.createNewPolygon(this.fieldStore.selectedField?.BoundaryPoints as Point[]);
    this.fieldStore.setSelectedFieldArea(this.findFieldArea());
  }

  @Watch('fieldStore.clearFieldMap')
  onClearMapChange(): void {
    if (this.polygon) {
      this.polygon.remove();
    }
  }

  @Watch('fieldStore.addMode')
  onAddPathChange(value: boolean): void {
    if (value) {
      if (this.polygon) {
        this.polygon.setLatLngs([]);
        this.polygon.addTo(this.map);
        const polyPoiints = this.polygon.toGeoJSON();
        this.fieldStore.updateCurrentPolygon(polyPoiints.geometry.coordinates as Position[][]);
        this.polygon.pm.enable({ removeVertexOn: 'click' });
      }
    }
  }

  addMapClickListener() {
    this.map?.off('click');
    this.polygon?.off('pm:edit');

    if (!this.map) return;
    if (this.polygon) {
      this.addPolygonEditListener();
    }
    this.map.on('click', (e: L.LeafletMouseEvent) => {
      if (!this.fieldStore.addFieldMode) return;

      if (!this.polygon) {
        this.createNewPolygon([]);
        this.addPolygonEditListener();
      }

      this.addPointToPath(e);
    });
  }

  private addPolygonEditListener() {
    if (this.fieldStore.editFieldMode || this.fieldStore.addFieldMode)
      this.polygon.pm.enable({ removeVertexOn: 'click' });
    else {
      this.polygon.pm.disable();
    }
    this.polygon.on('pm:edit', () => {
      this.fieldStore.setSelectedFieldArea(this.findFieldArea());
      const polyPoints = this.polygon.toGeoJSON();
      this.fieldStore.updateCurrentPolygon(polyPoints.geometry.coordinates as Position[][]);
    });
  }

  addPointToPath(e: L.LeafletMouseEvent) {
    const newPath = this.polygon.getLatLngs();
    if (newPath == undefined || newPath.length == 0) {
      this.polygon.setLatLngs([e.latlng]);
    } else {
      this.polygon.addLatLng(e.latlng);
    }

    this.fieldStore.setSelectedFieldArea(this.findFieldArea());
    const polyPoiints = this.polygon.toGeoJSON();
    this.fieldStore.updateCurrentPolygon(polyPoiints.geometry.coordinates as Position[][]);

    if (this.fieldStore.addFieldMode) {
      this.polygon.pm.enable({ removeVertexOn: 'click' });
    } else {
      this.polygon.pm.disable();
    }
  }

  /**
   * Returns the field area.
   */
  findFieldArea() {
    return this.polygon.getLatLngs() == undefined ? 0 : area(this.polygon.toGeoJSON());
  }
}
